"use client"

import { Package } from "lucide-react"

import { contentAnalytics } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"

/**
 * Pre-submission product picks for a /tools route.
 *
 * The tool surfaces that gate every affiliate link behind a completed form earn
 * almost nothing from the readers who never finish it, so this block renders
 * before the form runs. Shape and telemetry are the `tool-tank-mix-picks`
 * pattern from `app/tools/tank-mix-calculator/page-content.tsx`: one
 * `affiliate_product_shown` per row and one `affiliate_product_clicked` on the
 * same surface string, so CTR joins in PostHog.
 *
 * Thumbnails use a plain `img` (ready-to-use Amazon CDN URLs, no next/image
 * remote-pattern entry needed), matching `guide-sidebar-product.tsx`.
 *
 * Callers render this from the SERVER page shell where the calculator island is
 * Suspense-bailed by `useSearchParams`; otherwise the anchors never reach the
 * served HTML.
 */

export interface ToolPick {
  /** Full product name, as the Amazon listing calls it. */
  name: string
  /** Why this reader wants it. One noun phrase, no heading restatement. */
  note: string
  affiliateUrl: string
  imageUrl?: string
}

interface ToolPicksBlockProps {
  /** Unified affiliate surface, e.g. `tool-spreader-picks`. */
  surface: string
  /** Tool route slug, stamped as `article_slug` as `tool:<slug>`. */
  toolSlug: string
  /** Single concise label above the rows. No supporting copy. */
  label: string
  picks: ToolPick[]
}

function PickRow({
  pick,
  pickIndex,
  surface,
  toolSlug,
}: {
  pick: ToolPick
  pickIndex: number
  surface: string
  toolSlug: string
}) {
  const impressionRef = useAffiliateImpression(surface, {
    article_slug: `tool:${toolSlug}`,
    product_name: pick.name,
    affiliate_link: pick.affiliateUrl,
    pick_index: pickIndex,
  })

  return (
    <div
      ref={impressionRef}
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3"
    >
      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-gray-100 bg-white">
        {pick.imageUrl ? (
          <img
            src={pick.imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-contain p-1"
          />
        ) : (
          <Package className="h-5 w-5 text-gray-400" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900">{pick.name}</span>
        <span className="block text-[12.5px] text-gray-500">{pick.note}</span>
        <a
          href={pick.affiliateUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          data-affiliate="true"
          data-affiliate-placement={surface}
          onClick={() =>
            contentAnalytics.affiliateProductClicked(surface, {
              productName: pick.name,
              affiliateLink: pick.affiliateUrl,
              article_slug: `tool:${toolSlug}`,
              pick_index: pickIndex,
            })
          }
          className="block text-[12.5px] font-semibold text-green-600 hover:text-green-700"
        >
          Check price on Amazon →
        </a>
      </div>
    </div>
  )
}

export function ToolPicksBlock({ surface, toolSlug, label, picks }: ToolPicksBlockProps) {
  if (picks.length === 0) return null

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
      <p className="mb-2.5 text-[13px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <div className="flex flex-col gap-2">
        {picks.map((pick, i) => (
          <PickRow
            key={pick.name}
            pick={pick}
            pickIndex={i}
            surface={surface}
            toolSlug={toolSlug}
          />
        ))}
      </div>
      <p className="mt-3 text-xs text-gray-400">
        As an Amazon Associate, we earn from qualifying purchases.
      </p>
    </section>
  )
}
